import "./App.css";
import Layout from "./Layout";
import { Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import { CssBaseline } from "@mui/material";
import { BrowserRouter } from "react-router-dom";

import LoginPage from "../src/LoginPage";
import InjectAxiosInterceptors from "./plugin/InjectAxiosInterceptors";

// Lazy load SessionExpired (only needed on session timeout) for smaller initial bundle
const SessionExpired = lazy(() => import("./pages/general/SessionExpired"));
// Lazy load HostTimings (large page) to keep main bundle smaller and first load faster
const HostTimings = lazy(() => import("./pages/bookings/HostTimings"));

// Lazy load components for code splitting
const Dashboard = lazy(() => import("../src/pages/general/Dashboard"));
const AllBookings = lazy(() => import("./pages/bookings/AllBookings"));
const Live = lazy(() => import("./pages/bookings/OnGoingBookings"));
const Pending = lazy(() => import("./pages/bookings/PendingBookings"));
const Rejected = lazy(() => import("./pages/bookings/RejectedBookings"));
const Cancelled = lazy(() => import("./pages/bookings/CancelledBookings"));
const Failed = lazy(() => import("./pages/bookings/FailedBookings"));
const History = lazy(() => import("./pages/bookings/HistoryBookings"));
const TotalBookingEarnings = lazy(() => import("./pages/bookings/TotalEarnings"));
const CompleteTrip = lazy(() => import("./pages/bookings/CompleteTrip"));
const ListVehicles = lazy(() => import("./pages/vehicles/LiveVehicles"));
const AssignGps = lazy(() => import("./pages/vehicles/AssignGps"));
const Tracking = lazy(() => import("./pages/vehicles/Tracking"));
const AllCustomers = lazy(() => import("./pages/customers/AllCustomers"));
const BlockedCustomers = lazy(() => import("./pages/customers/BlockedCustomers"));
const ApproveHosts = lazy(() => import("./pages/hosts/ApproveHosts"));
const BlockedHosts = lazy(() => import("./pages/hosts/BlockedHosts"));
const HostVehiclePricing = lazy(() => import("./pages/hosts/HostVehiclePricing"));
const HostPayouts = lazy(() => import("./pages/hosts/HostPayouts"));
const BlockedVehicles = lazy(() => import("./pages/vehicles/BlockedVehicles"));
const LiveChat = lazy(() => import("./pages/support/LiveChat"));
const Mechanic = lazy(() => import("./pages/support/Mechanic"));
const Complaints = lazy(() => import("./pages/complaints/Complaints"));
const ProtectionPlans = lazy(() => import("./pages/appadmin/ProtectionPlans"));
const Coupons = lazy(() => import("./pages/appadmin/Coupons"));
const PreRegisteredUsers = lazy(() => import("./pages/customers/PreRegisteredUsers"));
const VehicleMake = lazy(() => import("./pages/vehicleadmin/VehicleMake"));
const VehicleModel = lazy(() => import("./pages/vehicleadmin/VehicleModel"));
const VehicleCategory = lazy(() => import("./pages/vehicleadmin/VehicleCategory"));
const VehicleFuelTypes = lazy(() => import("./pages/vehicleadmin/VehicleFuelTypes"));
const VehicleFeatures = lazy(() => import("./pages/vehicleadmin/VehicleFeatures"));
const VehicleTransmission = lazy(() => import("./pages/vehicleadmin/VehicleTransmission"));
const VerifyVehicles = lazy(() => import("./pages/vehicles/VerifyVehicles"));
const VehicleApproval = lazy(() => import("./pages/vehicles/ApprovalPage"));
const TotalEarnings = lazy(() => import("./pages/payments/TotalEarnings"));
const HostEarnings = lazy(() => import("./pages/payments/HostEarnings"));
const Refunds = lazy(() => import("./pages/payments/Refunds"));
const ReferralSystem = lazy(() => import("./pages/appadmin/ReferralSystem"));
const PauseVehicle = lazy(() => import("./pages/vehicles/PauseVehicle"));
const AgentList = lazy(() => import("./pages/agents/AgentList"));
const AgentPayments = lazy(() => import("./pages/agents/AgentPayments"));
const HostOffers = lazy(() => import("./pages/hosts/HostOffers"));
const HostsWithVehicles = lazy(() => import("./pages/hosts/HostsWithVehicles"));
const Upcoming = lazy(() => import("./pages/bookings/UpcomingBookings"));
const EndedTrips = lazy(() => import("./pages/bookings/EndedTrips"));
const Profile = lazy(() => import("./pages/general/Profile"));
const Cities = lazy(() => import("./pages/cities/Cities"));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <InjectAxiosInterceptors />
          <Routes>
            <Route path="/" element={<LoginPage />}>
              <Route path="login" element={<LoginPage />}></Route>
              <Route path="sessiontimeout" element={<Suspense fallback={null}><SessionExpired /></Suspense>}></Route>
            </Route>

            <Route path="/main" element={<Layout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="preregistered" element={<PreRegisteredUsers />} />
              <Route path="bookings/all" element={<AllBookings />} />
              <Route path="bookings/live" element={<Live />} />
              <Route path="bookings/pending" element={<Pending />} />
              <Route path="bookings/upcoming" element={<Upcoming />} />
              <Route path="bookings/endedtrips" element={<EndedTrips />} />
              <Route path="bookings/rejected" element={<Rejected />} />
              <Route path="bookings/cancelled" element={<Cancelled />} />
              <Route path="bookings/failed" element={<Failed />} />
              <Route path="bookings/history" element={<History />} />
              <Route path="bookings/completetrip" element={<CompleteTrip />} />
              <Route path="bookings/hosttimings" element={<Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>Loading…</div>}><HostTimings /></Suspense>} />
              <Route
                path="bookings/totalearnings"
                element={<TotalBookingEarnings />}
              />

              <Route path="customers/all" element={<AllCustomers />} />
              <Route path="customers/blocked" element={<BlockedCustomers />} />

              <Route path="host/approve" element={<ApproveHosts />} />
              <Route
                path="host/vehiclepricing"
                element={<HostVehiclePricing />}
              />
              <Route path="host/details" element={<HostsWithVehicles />} />
              <Route path="host/blocked" element={<BlockedHosts />} />
              <Route path="host/hostpayouts" element={<HostPayouts />} />
              <Route path="host/offers" element={<HostOffers />} />

              <Route path="agent/all" element={<AgentList />} />
              <Route path="agent/payments" element={<AgentPayments />} />

              <Route path="vehicles/pause" element={<PauseVehicle />} />
              <Route path="vehicles/list" element={<ListVehicles />} />
              <Route path="vehicles/verify" element={<VerifyVehicles />} />
              <Route path="vehicles/approval" element={<VehicleApproval />} />
              <Route path="vehicles/assigngps" element={<AssignGps />} />
              <Route path="vehicles/tracking" element={<Tracking />} />
              <Route path="vehicles/blocked" element={<BlockedVehicles />} />

              <Route
                path="payments/totalearnings"
                element={<TotalEarnings />}
              />
              <Route path="payments/hostearnings" element={<HostEarnings />} />

              <Route path="payments/refunds" element={<Refunds />} />

              <Route path="support/livechat" element={<LiveChat />} />
              <Route path="support/mechanic" element={<Mechanic />} />

              <Route path="vadmin/vehiclemake" element={<VehicleMake />} />
              <Route path="vadmin/vehiclemodel" element={<VehicleModel />} />

              <Route
                path="vadmin/vehiclecategory"
                element={<VehicleCategory />}
              />
              <Route
                path="vadmin/vehiclefueltype"
                element={<VehicleFuelTypes />}
              />
              <Route
                path="vadmin/vehiclefeatures"
                element={<VehicleFeatures />}
              />
              <Route
                path="vadmin/vehicletransmission"
                element={<VehicleTransmission />}
              />
              <Route path="vadmin/cities" element={<Cities />} />

              <Route path="complaints" element={<Complaints />} />

              <Route path="aadmin/coupons" element={<Coupons />} />
              <Route path="aadmin/referral" element={<ReferralSystem/>}/>
              <Route
                path="aadmin/protectionplans"
                element={<ProtectionPlans />}
              />

              {/* <Route path="carrentaldboard" element={<CarrentalDashboard />} />
            <Route path="inventory">
            <Route path="drivers">
                <Route path="view" element={<Drivers />}></Route>
                <Route path="profile" element={<DriverProfile />}></Route>
                <Route path="add" element={<AddDrivers />}></Route>
              </Route>
            <Route path="vehicles">
                <Route path="view" element={<Vehicles />}></Route>
                <Route path="profile" element={<VehicleProfile />}></Route>
                <Route path="add" element={<AddVehicle />}></Route>
                <Route path="assigndriver" element={<AssigndriverToVehicle />}></Route>
              </Route>

              <Route path="corporates">
                <Route path="view" element={<Corporates />}></Route>
                <Route path="add" element={<AddCorproate />}></Route>
                <Route path="edit" element={<AddCorproate />}></Route>
              </Route>
              <Route path="geoboundary" element={<CreateGeoboundary />} />
              <Route path="users" element={<Users />} />
            </Route>
            <Route path="invoice">
               <Route path="view" element={<InvoiceList/>}/>
              <Route path="create" element={<CreateInvoice/>}/>
              <Route path="details/:InvoiceNumber" element={<ViewInvoice/>}/>
            </Route>
            <Route path="pricing" element={<PricingModel />} />
            <Route path="pricingV1" element={<PricingModelV1 />} />
            <Route path="tracking">
              <Route path="live" element={<LiveTracking />} />
              <Route path="history" element={<HistoryTracking />} />
            </Route>
            <Route path="booking">
              <Route path="create" element={<CreateBooking />} />
              <Route path="view/:bookingid" element={<BookingView />} />
              <Route path="oldList" element={<Bookings />} />
              <Route path="list" element={<NewBookingList />} />
              <Route path="dispatacher" element={<Dispatchers />} />
            </Route>
            <Route path="servicereminder" element={<ServiceReminder />}></Route>
            <Route path="renewals" element={<VehicleRenewals />}></Route>
            <Route path="reports" element={<Reports />}></Route>
            <Route path="settings" element={<Settings />}></Route>*/}
            </Route>
            {/* <Route path="/admin" element={<CreateOperators />} /> */}
          </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
